import { Button, Stack, Typography } from '@mui/material'
import { FC, forwardRef, useCallback } from 'react'
import {
  AvatarUploadField,
  AvatarUploadFieldProps,
  TextInputField,
  TextInputFieldProps,
} from 'utoniq-ui'

import {
  Form,
  handleError,
  tLabel,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

export type ProfileInputArtistFormProps = {
  hiddenDisplayName: boolean
  hiddenAccountId: boolean
  hiddenInviteCode: boolean
  onSubmit: (values: ProfileInputArtistValues) => Promise<void>
  onClickAvatar: AvatarUploadFieldProps['onClick']
}

type ProfileInputArtistValues = {
  avatarPath: string | null
  displayName: string
  accountId: string
  invitationCode: string
}

const schema: UseFormProps['schema'] = o => ({
  avatarPath: o
    .string()
    .optional()
    .allow('', null)
    .label(tLabel('profileInputArtist.avatarPathTitle')),
  displayName: o
    .string()
    .max(50)
    .optional()
    .allow('')
    .label(tLabel('profileInputArtist.displayName')),
  accountId: o
    .string()
    .pattern(new RegExp(/^[a-zA-Z0-9_]*$/))
    .max(20)
    .optional()
    .allow('')
    .label(tLabel('profileInputArtist.accountId')),
  invitationCode: o
    .string()
    .length(8)
    .optional()
    .allow('')
    .label(tLabel('profileInputArtist.invitationCode')),
})

export const ProfileInputArtistForm: FC<ProfileInputArtistFormProps> = ({
  hiddenDisplayName,
  hiddenAccountId,
  hiddenInviteCode,
  onSubmit,
  onClickAvatar,
}) => {
  const { t } = useTranslation('profileInputArtist')
  const { showError } = useMessage()

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setValue,
    setError,
  } = useForm<ProfileInputArtistValues>({
    schema,
    defaultValues: {
      avatarPath: '',
      displayName: '',
      accountId: '',
      invitationCode: '',
    },
  })

  const handleOnClick = useCallback(async () => {
    const result = await onClickAvatar()
    if (!result) {
      return
    }
    setValue('avatarPath', result?.tmpPath ?? null, {
      shouldValidate: true,
    })
    return result
  }, [onClickAvatar, setValue])

  const handleOnSubmit = handleSubmit(values =>
    onSubmit(values).catch(e =>
      handleError<ProfileInputArtistValues>(e, setError, showError)
    )
  )

  return (
    <Stack sx={{ pb: 4 }}>
      <Form onSubmit={handleOnSubmit} sx={{ pt: 3 }}>
        <AvatarUploadCell
          onClick={handleOnClick}
          sx={{ pt: 5, pb: 2 }}
          avatarProps={{ sx: { width: 146, height: 146 } }}
          titleProps={{
            color: 'primary.main',
            sx: { pt: 3 },
            variant: 'subtitle1',
          }}
          type="image"
          error={errors?.avatarPath}
          {...register('avatarPath')}
        />
        {!hiddenDisplayName && (
          <TextInputCell
            type="text"
            error={errors?.displayName}
            {...register('displayName')}
          />
        )}
        {!hiddenAccountId && (
          <TextInputCell
            type="accountId"
            error={errors?.accountId}
            {...register('accountId')}
          />
        )}
        {!hiddenInviteCode && (
          <TextInputCell
            error={errors?.invitationCode}
            type="number"
            titleProps={{
              children: (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle1" color="text.secondary">
                    {t('invitationCode')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({t('invitationCodeNote')})
                  </Typography>
                </Stack>
              ),
            }}
            {...register('invitationCode')}
          />
        )}

        <ButtonCell loading={isSubmitting} />
      </Form>
    </Stack>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('profileInputArtist')

    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

type ButtonCellProps = {
  loading: boolean
}
const ButtonCell: Cell<ButtonCellProps> = ({ loading }) => {
  const { t } = useTranslation('profileInputArtist')

  return (
    <CellFrame sx={{ py: 2, textAlign: 'center' }}>
      <Button type="submit" size="large" sx={{ width: 244 }} loading={loading}>
        {t('submit')}
      </Button>
    </CellFrame>
  )
}

const AvatarUploadCell = forwardRef<
  HTMLInputElement,
  Omit<AvatarUploadFieldProps, 't'>
>((props, ref) => {
  const { t } = useTranslation('profileInputArtist')
  return (
    <CellFrame sx={{ pb: 3 }}>
      <AvatarUploadField {...props} ref={ref} t={t} />
    </CellFrame>
  )
})
